import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Buildings, MapPin, Plus, MagnifyingGlass, SealCheck } from "@phosphor-icons/react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { NotFindingInstitution } from "@/components/NotFindingInstitution";
import { useAuth } from "@/context/AuthContext";
import InstitutionLocationFinder from "@/components/InstitutionLocationFinder";
import SaveInstitutionButton from "@/components/SaveInstitutionButton";

const TYPES = ["University", "College", "Engineering College", "High School", "School", "Institute"];

export default function Institutions() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [loc, setLoc] = useState({ country: "", state: "", city: "" });
  const [savedIds, setSavedIds] = useState(new Set());
  const [data, setData] = useState({ total: 0, results: [] });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", type: "University", location: "", country: "", description: "", website: "", founded: "" });

  const load = () => api.get("/institutions", { params: { q, country: loc.country, state: loc.state, city: loc.city, limit: 60 } }).then((r) => setData(r.data));
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, loc]);
  useEffect(() => { if (user) api.get("/profiles/me").then((r) => setSavedIds(new Set((r.data.saved_institutions || []).map((s) => s.institution_id)))).catch(() => {}); }, [user]);
  const toggleSaved = (id, isSaved) => setSavedIds((prev) => { const n = new Set(prev); isSaved ? n.add(id) : n.delete(id); return n; });

  const create = async () => {
    if (!form.name) return toast.error("Name is required");
    try {
      await api.post("/institutions", form);
      toast.success("Institution added");
      setOpen(false); setForm({ name: "", type: "University", location: "", country: "", description: "", website: "", founded: "" });
      load();
    } catch { toast.error("Could not add institution"); }
  };

  return (
    <main className="mx-auto max-w-7xl px-5 py-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="overline text-secondary mb-1">The institution database</p>
          <h1 className="font-heading text-3xl font-light tracking-tight sm:text-4xl">Institutions</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="rounded-full gap-2" data-testid="add-institution-btn"><Plus size={16} /> Add institution</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Add an institution</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="inst-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Type</Label>
                  <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>{TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>Founded</Label><Input value={form.founded} onChange={(e) => setForm({ ...form, founded: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                <div className="space-y-1.5"><Label>Country</Label><Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
              </div>
              <div className="space-y-1.5"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
            </div>
            <DialogFooter><Button onClick={create} className="rounded-full" data-testid="inst-submit">Add institution</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative mt-6 max-w-md">
        <MagnifyingGlass className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search institutions…" className="pl-11 rounded-full" data-testid="inst-search" />
      </div>
      <div className="mt-3 rounded-2xl border border-slate-100 border-t-4 border-t-green-600 bg-white p-4 shadow-sm">
        <InstitutionLocationFinder value={loc} onChange={setLoc} />
      </div>

      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {data.results.map((inst, i) => (
          <div key={inst.id} className="relative" data-testid="institution-card">
            <Link to={`/institutions/${inst.id}`}>
              <Card className="group h-full overflow-hidden border-slate-100 shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-md animate-fade-up" style={{ animationDelay: `${i * 0.03}s` }}>
                <div className="relative flex h-32 items-center justify-center overflow-hidden bg-gradient-to-br from-green-50 via-white to-orange-50">
                  {inst.logo_url ? <img src={inst.logo_url} alt={inst.name} className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105" /> : <Buildings size={40} weight="duotone" className="text-green-600/70" />}
                </div>
                <div className="p-5 pb-14">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-heading text-lg font-medium leading-tight">{inst.name}</h3>
                    {inst.verified ? <SealCheck className="shrink-0 text-green-600" size={18} weight="fill" /> : null}
                  </div>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <Badge className="rounded-full border-green-200 bg-green-50 font-normal text-green-800" variant="outline">{inst.type}</Badge>
                    {!inst.verified && <Badge variant="outline" className="rounded-full font-normal text-xs text-muted-foreground">Unverified</Badge>}
                  </div>
                  {inst.location && <p className="mt-3 flex items-center gap-1.5 text-sm text-muted-foreground"><MapPin size={14} className="text-orange-500" /> {inst.location}</p>}
                </div>
              </Card>
            </Link>
            <div className="absolute bottom-4 left-5">
              <SaveInstitutionButton instId={inst.id} saved={savedIds.has(inst.id)} onToggle={toggleSaved} />
            </div>
          </div>
        ))}
      </div>

      <NotFindingInstitution className="mt-12" />
    </main>
  );
}
